import React, { useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import PreviewCanvas from './PreviewCanvas';
import { ApplyVideoWorkspaceBlocks } from '../../../Client/ManagementServiceClient';
import './effectBlocks.css';

const DEFAULT_PARAMS = { x: 0.5, y: 0.5, scale: 0.25 };

/**
 * StickerBlockEditor — phase-2 effect block: overlay an image asset (logo,
 * sticker) on the clip. Pick an image from the workspace, place and size it,
 * then compile via ApplyBlocks -> scale_tool + overlay_tool.
 *
 * Props:
 *   videoWorkspaceId, frameUrl, sourceFileName, mediaInfo  - the target video
 *   imageAssets   - [{ FileName, FileAlias }] of the workspace's image assets
 *   resolveAssetUrl - async (asset) => url|null, to preview a picked image
 *   onApplied, onClose
 */
function StickerBlockEditor({ videoWorkspaceId, frameUrl, sourceFileName, mediaInfo, imageAssets = [], resolveAssetUrl, onApplied, onClose }) {
    const [params, setParams] = useState(DEFAULT_PARAMS);
    const [applying, setApplying] = useState(false);
    const [error, setError] = useState('');
    const [measured, setMeasured] = useState(() => (
        Number(mediaInfo?.width) > 0 && Number(mediaInfo?.height) > 0 ? mediaInfo : null
    ));
    const [stickerFile, setStickerFile] = useState('');
    const [stickerUrl, setStickerUrl] = useState('');
    const [stickerDims, setStickerDims] = useState(null); // { width, height }
    const [loadingSticker, setLoadingSticker] = useState(false);

    const aspectRatio = useMemo(() => {
        const w = Number(measured?.width) || 9;
        const h = Number(measured?.height) || 16;
        return w / h;
    }, [measured]);

    const block = useMemo(
        () => ({ id: 'sticker-1', type: 'sticker', source: sourceFileName, params: { ...params, overlayAsset: stickerFile } }),
        [params, sourceFileName, stickerFile],
    );

    const update = (patch) => setParams((prev) => ({ ...prev, ...patch }));

    const pickSticker = async (fileName) => {
        setStickerFile(fileName);
        setStickerUrl('');
        setStickerDims(null);
        if (!fileName) return;
        const asset = imageAssets.find((a) => a.FileName === fileName);
        if (!asset || !resolveAssetUrl) return;
        try {
            setLoadingSticker(true);
            const url = await resolveAssetUrl(asset);
            if (!url) {
                setError('Could not load that image.');
                return;
            }
            // Measure the image's real dimensions so the overlay resolves to pixels.
            const dims = await new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
                img.onerror = reject;
                img.src = url;
            });
            setStickerUrl(url);
            setStickerDims(dims);
            setError('');
        } catch {
            setError('Could not load that image.');
        } finally {
            setLoadingSticker(false);
        }
    };

    const canApply = Boolean(
        videoWorkspaceId && sourceFileName && stickerFile &&
        Number(measured?.width) > 0 && Number(measured?.height) > 0 &&
        Number(stickerDims?.width) > 0 && Number(stickerDims?.height) > 0,
    );

    const handleApply = async () => {
        if (!canApply) {
            setError('Pick an image and make sure both it and the clip have loaded.');
            return;
        }
        setApplying(true);
        setError('');
        try {
            const idToken = await getAuth().currentUser.getIdToken();
            const outBlock = {
                id: 'sticker-1',
                type: 'sticker',
                source: sourceFileName,
                startTime: 0,
                endTime: null,
                params: { x: params.x, y: params.y, scale: params.scale, overlayAsset: stickerFile },
            };
            const media = {
                [sourceFileName]: {
                    width: Math.round(Number(measured.width)),
                    height: Math.round(Number(measured.height)),
                    duration: Number(measured.duration) || 0,
                },
                [stickerFile]: {
                    width: Math.round(Number(stickerDims.width)),
                    height: Math.round(Number(stickerDims.height)),
                    duration: 0,
                },
            };
            const res = await ApplyVideoWorkspaceBlocks(idToken, videoWorkspaceId, [outBlock], media);
            if (res.status === 200) {
                onApplied?.(res.myJson);
            } else {
                setError(res.myJson?.detail || 'Failed to apply the sticker.');
            }
        } catch (err) {
            setError('Error applying edit: ' + err.message);
        } finally {
            setApplying(false);
        }
    };

    return (
        <div className="eb-editor">
            <div className="eb-editor-grid">
                <PreviewCanvas
                    frameUrl={frameUrl}
                    isVideo
                    aspectRatio={aspectRatio}
                    blocks={[block]}
                    selectedId="sticker-1"
                    stickerUrl={stickerUrl}
                    onChange={(_id, next) => setParams((prev) => ({ ...prev, ...next }))}
                    onSelect={() => {}}
                    onMeta={setMeasured}
                />
                <div className="eb-controls">
                    <h5>Sticker</h5>

                    <label className="eb-ctl">
                        <span>Image</span>
                        <select className="eb-input" value={stickerFile} onChange={(e) => pickSticker(e.target.value)}>
                            <option value="">Choose an image…</option>
                            {imageAssets.map((a) => (
                                <option key={a.FileName} value={a.FileName}>{a.FileAlias || a.FileName}</option>
                            ))}
                        </select>
                    </label>

                    {imageAssets.length === 0 && (
                        <p className="eb-hint">Upload an image to this project first, then it appears here.</p>
                    )}
                    {loadingSticker && <p className="eb-hint">Loading image…</p>}

                    <label className="eb-ctl">
                        <span>Size <em>{Math.round((params.scale || 0) * 100)}%</em></span>
                        <input
                            type="range" min="5" max="80" step="1"
                            value={(params.scale || 0.25) * 100}
                            onChange={(e) => update({ scale: Number(e.target.value) / 100 })}
                        />
                    </label>

                    <p className="eb-hint">Drag the image on the canvas to position it.</p>

                    {error && <p className="eb-error">{error}</p>}

                    <div className="eb-actions">
                        {onClose && <button type="button" className="eb-btn" onClick={onClose}>Cancel</button>}
                        <button type="button" className="eb-btn primary" disabled={!canApply || applying} onClick={handleApply}>
                            {applying ? 'Applying…' : 'Add to timeline'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default StickerBlockEditor;
